import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Splitter, { SplitDirection } from "@devbookhq/splitter";
import Editor from "@monaco-editor/react";
import *as Blockly from "blockly";
import "bootstrap/dist/css/bootstrap.min.css";
import SimpleIDESimulation from "./SimpleIDESimulation";
import { useDataProvider } from "../../hooks/data/DataProvider";

import "./blockly/spheres/blocks/zrms17"
import "./blockly/spheres/generator/zr_cpp/zrms17"
import "./blockly/spheres/generator/zr_cpp/procedures"
import "./blockly/spheres/generator/zr_cpp/variables"
import "./blockly/spheres/generator/zr_cpp/logic"
import "./blockly/spheres/generator/zr_cpp/math"
import "./blockly/spheres/generator/zr_cpp/zr-base-api"
import "./blockly/spheres/blocks/variablesArray";
import "./blockly/spheres/spheresBlocks";
import "./blockly/spheres/standardBlocks";
import "./blockly/spheres/variablesBlocks";
import { zrCppGenerator } from "./blockly/spheres/generator/zr_cpp/zrCppGenerator";

const BLOCKLY_GENERATORS = { "spheres.move_to": zrCppGenerator };
const getBlocklyGenerator = c => c?.generator ? BLOCKLY_GENERATORS[c.generator] || null : null;


function createCommonProjectCode(project) {
    const source = parseProjectCode(project);

    let code;

    try {
        code =
            typeof structuredClone === "function"
                ? structuredClone(source)
                : JSON.parse(JSON.stringify(source));
    } catch (error) {
        code = { ...source };
    }

    if (!code || typeof code !== "object" || Array.isArray(code)) {
        code = {};
    }

    // ------------------------------------------------------------
    // Basic/common fields
    // ------------------------------------------------------------

    if (code.gameId == null) {
        code.gameId = "";
    }

    if (
        code.editorMode !== "text" &&
        code.editorMode !== "graphical"
    ) {
        code.editorMode =
            code.graphical === true
                ? "graphical"
                : "text";
    }

    if (code.teamShared == null) {
        code.teamShared = false;
    }

    if (code.graphical == null) {
        code.graphical =
            code.editorMode === "graphical";
    }

    // ------------------------------------------------------------
    // Pages
    // ------------------------------------------------------------

    if (!code.pages || typeof code.pages !== "object") {
        code.pages = {};
    }

    if (
        !code.pages.pairs ||
        typeof code.pages.pairs !== "object"
    ) {
        code.pages.pairs = {};
    }

    // ------------------------------------------------------------
    // Text files
    // ------------------------------------------------------------

    if (
        !code.files ||
        typeof code.files !== "object"
    ) {
        code.files = {};
    }

    /*
     * Text projects may currently store their files under
     * pages.pairs instead of files.
     *
     * Only populate files when files is empty.
     * This preserves an existing files structure.
     */
    if (
        code.editorMode === "text" &&
        Object.keys(code.files).length === 0
    ) {
        code.files = extractTextFilesFromPages(
            code.pages
        );
    }

    // ------------------------------------------------------------
    // Graphical sections
    // ------------------------------------------------------------

    if (!code.pages.pairs.main) {
        code.pages.pairs.main = {
            pairs: {
                xml: "",
                dom: {},
            },
        };
    }

    if (!code.pages.pairs.init) {
        code.pages.pairs.init = {
            pairs: {
                xml: "",
                dom: {},
            },
        };
    }

    // ------------------------------------------------------------
    // Common sections
    // ------------------------------------------------------------

    if (!code.roLines || typeof code.roLines !== "object") {
        code.roLines = {
            pairs: {},
        };
    }

    if (!code.cglobals || typeof code.cglobals !== "object") {
        code.cglobals = {
            pairs: {},
        };
    }

    if (!code.procedures || typeof code.procedures !== "object") {
        code.procedures = {
            pairs: {},
        };
    }

    if (!code.log || typeof code.log !== "object") {
        code.log = {
            pairs: {},
        };
    }

    if (!code.log.pairs || typeof code.log.pairs !== "object") {
        code.log.pairs = {};
    }

    if (
        !code.activities ||
        typeof code.activities !== "object"
    ) {
        code.activities = {
            pairs: {},
        };
    }

    if (
        !code.activities.pairs ||
        typeof code.activities.pairs !== "object"
    ) {
        code.activities.pairs = {};
    }

    return code;
}

function getProjectLogs(codeData) {
    const pairs = codeData?.log?.pairs;

    if (!pairs || typeof pairs !== "object") {
        return [];
    }

    return Object.entries(pairs)
        .sort(([a], [b]) => Number(a) - Number(b));
}

function getGameConfig(p) {
    const g = p?.game || {};
    //console.log("SIMPLE IDE XXXXX    ", p.id)
    return {
        id: g.id, name: g.name || "",
        displayName: g.displayName ?? g.display_name ?? g.name ?? "Game",
        robotType: g.robotType ?? g.robot_type ?? "",
        language: g.language ?? "plaintext",
        mainFile: g.mainFile ?? g.main_file ?? "",
        compileType: g.compileType ?? g.compile_type ?? "",
        ideType: g.ideType ?? g.ide_type ?? "simple",
        repository: g.repository ?? g.git_repository ?? "",
        gitBranch: g.gitBranch ?? g.git_branch ?? "main",
        projectPath: g.project_path ?? "",
        apiConfigs: g.api_configs ?? g.apiConfigs ?? [],
        blocklyConfig: g.blockly_config ?? g.blocklyConfig ?? null
    };
}

function getEditorMode(project) {
    const code = parseProjectCode(project);

    if (typeof code?.editorMode === "string") {
        const mode = code.editorMode.toLowerCase();

        if (mode === "graphical" || mode === "text") {
            return mode;
        }
    }

    if (typeof code?.graphical === "boolean") {
        return code.graphical ? "graphical" : "text";
    }

    // Backward compatibility with existing projects
    return String(project?.level || "").toLowerCase() === "beginner"
        ? "graphical"
        : "text";
}

async function api(url, options = {}) {
    const r = await fetch(url, { credentials: "include", ...options });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.error || data?.message || `Request failed: ${r.status}`);
    return data;
}

const fetchProjectFiles = id => api(`/api/project/${id}/files`);

const saveProjectFiles = (id, files) => api(`/api/project/${id}/files`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ files })
});

const commitProject = (id, message) => api(`/api/project/${id}/git/commit`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message })
});

const normalizePath = (p = "") => String(p).replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
const getParentPath = p => { p = normalizePath(p); const i = p.lastIndexOf("/"); return i < 0 ? "" : p.substring(0, i) };
const getFileName = p => { p = normalizePath(p); const i = p.lastIndexOf("/"); return i < 0 ? p : p.substring(i + 1) };

function pathExists(files, path) {
    if (!path) return false;
    let cur = files;
    for (const p of normalizePath(path).split("/").filter(Boolean)) {
        if (!cur || typeof cur !== "object" || !Object.prototype.hasOwnProperty.call(cur, p)) return false;
        cur = cur[p];
    }
    return true;
}

function getFileFromTree(files, path) {
    if (!files || !path) return null;
    let cur = files;
    for (const p of normalizePath(path).split("/").filter(Boolean)) {
        if (!cur || typeof cur !== "object" || !Object.prototype.hasOwnProperty.call(cur, p)) return null;
        cur = cur[p];
    }
    return typeof cur === "string" ? cur : null;
}

function setFileInTree(files, path, content = "") {
    const result = { ...(files || {}) }, parts = normalizePath(path).split("/").filter(Boolean);
    if (!parts.length) return result;
    let cur = result;
    parts.slice(0, -1).forEach(p => {
        cur[p] = cur[p] && typeof cur[p] === "object" && !Array.isArray(cur[p]) ? { ...cur[p] } : {};
        cur = cur[p];
    });
    cur[parts.at(-1)] = content;
    return result;
}

function createFolderInTree(files, path) {
    const result = { ...(files || {}) }, parts = normalizePath(path).split("/").filter(Boolean);
    if (!parts.length) return result;
    let cur = result;
    parts.forEach(p => {
        cur[p] = cur[p] && typeof cur[p] === "object" && !Array.isArray(cur[p]) ? { ...cur[p] } : {};
        cur = cur[p];
    });
    return result;
}

const createFileInTree = (files, path, content = "") => setFileInTree(files, path, content);

function deleteFromTree(files, path) {
    const result = { ...(files || {}) }, parts = normalizePath(path).split("/").filter(Boolean);
    if (!parts.length) return result;
    if (parts.length === 1) { delete result[parts[0]]; return result }
    let cur = result;
    for (const p of parts.slice(0, -1)) {
        if (!cur[p] || typeof cur[p] !== "object") return result;
        cur[p] = { ...cur[p] }; cur = cur[p];
    }
    delete cur[parts.at(-1)];
    return result;
}

function renameTreeItem(files, oldPath, newName) {
    const old = normalizePath(oldPath), name = normalizePath(newName);
    if (!old || !name) return files;
    let cur = files;
    for (const p of old.split("/").filter(Boolean)) {
        if (!cur || typeof cur !== "object" || !Object.prototype.hasOwnProperty.call(cur, p)) return files;
        cur = cur[p];
    }
    const parent = getParentPath(old), newPath = parent ? `${parent}/${name}` : name;
    if (pathExists(files, newPath)) return files;
    let result = deleteFromTree(files, old);
    if (typeof cur === "string") return createFileInTree(result, newPath, cur);
    result = createFolderInTree(result, newPath);
    const copy = (v, target) => {
        Object.entries(v || {}).forEach(([k, c]) => {
            const p = `${target}/${k}`;
            if (c && typeof c === "object" && !Array.isArray(c)) {
                result = createFolderInTree(result, p); copy(c, p);
            } else result = createFileInTree(result, p, c);
        });
    };
    copy(cur, newPath);
    return result;
}

function findFirstFile(structure, parent = "") {
    for (const [name, value] of Object.entries(structure || {})) {
        const path = parent ? `${parent}/${name}` : name;
        if (value && typeof value === "object" && !Array.isArray(value)) {
            const r = findFirstFile(value, path); if (r) return r;
        } else return path;
    }
    return null;
}

const getLanguage = p => {
    const ext = p.toLowerCase().split(".").pop();
    return {
        c: "c", cpp: "cpp", cc: "cpp", cxx: "cpp", h: "cpp", hpp: "cpp",
        java: "java", js: "javascript", jsx: "javascript", ts: "typescript",
        tsx: "typescript", py: "python", json: "json", xml: "xml", html: "html",
        css: "css", md: "markdown", yml: "yaml", yaml: "yaml", sh: "shell",
        bash: "shell", gradle: "groovy"
    }[ext] || "plaintext";
};

function buildTree(structure, parent = "") {
    if (!structure || typeof structure !== "object") return [];
    return Object.keys(structure).sort((a, b) => {
        const af = structure[a] && typeof structure[a] === "object" && !Array.isArray(structure[a]);
        const bf = structure[b] && typeof structure[b] === "object" && !Array.isArray(structure[b]);
        return af !== bf ? (af ? -1 : 1) : a.localeCompare(b);
    }).map(name => {
        const value = structure[name], isFolder = value && typeof value === "object" && !Array.isArray(value);
        const path = parent ? `${parent}/${name}` : name;
        return { key: path, title: name, isFolder, isLeaf: !isFolder, children: isFolder ? buildTree(value, path) : [] };
    });
}

const emptyCode = () => ({ pages: { pairs: {} } });

function parseProjectCode(project) {
    /*
     * Accepts:
     *
     * 1. A Project object:
     *      { id: 123, code: "{\"gameId\":\"30\", ...}" }
     *
     * 2. A Project object whose code is already an object:
     *      { id: 123, code: { gameId: "30", ... } }
     *
     * 3. A JSON string:
     *      "{\"gameId\":\"30\", ...}"
     *
     * 4. An already parsed project-code object:
     *      { gameId: "30", ... }
     *
     * 5. Missing/null/empty/invalid code
     *
     * Returns:
     *      A safe plain object.
     *
     * It does NOT add the common Project.code sections.
     * That is handled by createCommonProjectCode().
     */

    if (project == null) {
        return {};
    }

    let value;

    // ------------------------------------------------------------
    // If this looks like a Project object, use project.code.
    //
    // We check for "code" explicitly so that an already-parsed
    // Project.code object is not mistaken for a Project object.
    // ------------------------------------------------------------

    if (
        typeof project === "object" &&
        !Array.isArray(project) &&
        Object.prototype.hasOwnProperty.call(project, "code")
    ) {
        value = project.code;
    } else {
        // Otherwise, assume the caller passed Project.code itself
        // or a JSON string containing Project.code.
        value = project;
    }

    // ------------------------------------------------------------
    // Missing code
    // ------------------------------------------------------------

    if (value == null) {
        return {};
    }

    // ------------------------------------------------------------
    // Empty string
    // ------------------------------------------------------------

    if (
        typeof value === "string" &&
        value.trim() === ""
    ) {
        return {};
    }

    // ------------------------------------------------------------
    // Parse JSON strings.
    //
    // Some older Project.code values may have been encoded more
    // than once, so allow several decoding passes.
    // ------------------------------------------------------------

    if (typeof value === "string") {
        let parsed = value;

        for (let i = 0; i < 3; i++) {
            if (typeof parsed !== "string") {
                break;
            }

            const trimmed = parsed.trim();

            if (!trimmed) {
                return {};
            }

            try {
                parsed = JSON.parse(trimmed);
            } catch (error) {
                console.warn(
                    "Unable to parse Project.code JSON:",
                    error
                );

                return {};
            }
        }

        value = parsed;
    }

    // ------------------------------------------------------------
    // The final value must be an object.
    //
    // Arrays are not valid Project.code structures.
    // ------------------------------------------------------------

    if (
        value === null ||
        typeof value !== "object" ||
        Array.isArray(value)
    ) {
        return {};
    }

    // ------------------------------------------------------------
    // Make sure the returned object is a plain independent object.
    //
    // createCommonProjectCode() will perform the final deep clone,
    // but cloning here protects callers that modify the result
    // directly.
    // ------------------------------------------------------------

    try {
        if (typeof structuredClone === "function") {
            return structuredClone(value);
        }

        return JSON.parse(JSON.stringify(value));
    } catch (error) {
        console.warn(
            "Unable to clone Project.code:",
            error
        );

        /*
         * Last-resort shallow copy.
         *
         * This keeps the function safe even if an unusual value
         * cannot be cloned. Normal Project.code JSON should never
         * reach this path.
         */
        return {
            ...value
        };
    }
}

function extractTextFilesFromPages(pages) {
    let files = {};

    const pairs = pages?.pairs;

    if (!pairs || typeof pairs !== "object") {
        return files;
    }

    const walk = (node, path = "") => {
        if (!node || typeof node !== "object") {
            return;
        }

        for (const [key, value] of Object.entries(node)) {
            if (key === "pairs" && value && typeof value === "object") {
                walk(value, path);
                continue;
            }

            const currentPath = path
                ? `${path}/${key}`
                : key;

            if (value && typeof value === "object") {
                const content =
                    value.text ??
                    value.content ??
                    value.code;

                if (typeof content === "string") {
                    files = setFileInTree(
                        files,
                        currentPath,
                        content
                    );

                    continue;
                }

                walk(value, currentPath);
            }
        }
    };

    walk(pairs);

    return files;
}

function getBlocklyXml(code) {
    const p = code?.pages?.pairs || {};
    return { mainXml: p?.main?.pairs?.xml || "", initXml: p?.init?.pairs?.xml || "" };
}

function setBlocklyPageXml(projectCode, page, xml) {
    if (!projectCode) {
        return;
    }

    if (
        !projectCode.pages ||
        typeof projectCode.pages !== "object" ||
        Array.isArray(projectCode.pages)
    ) {
        projectCode.pages = {};
    }

    if (
        !projectCode.pages.pairs ||
        typeof projectCode.pages.pairs !== "object" ||
        Array.isArray(projectCode.pages.pairs)
    ) {
        projectCode.pages.pairs = {};
    }

    const pageName = String(page || "")
        .replace(/\.xml$/i, "");

    if (!pageName) {
        return;
    }

    if (
        !projectCode.pages.pairs[pageName] ||
        typeof projectCode.pages.pairs[pageName] !== "object"
    ) {
        projectCode.pages.pairs[pageName] = {
            pairs: {}
        };
    }

    if (
        !projectCode.pages.pairs[pageName].pairs ||
        typeof projectCode.pages.pairs[pageName].pairs !== "object"
    ) {
        projectCode.pages.pairs[pageName].pairs = {};
    }

    projectCode.pages.pairs[pageName].pairs.xml = xml || "";

    if (
        projectCode.pages.pairs[pageName].pairs.dom == null
    ) {
        projectCode.pages.pairs[pageName].pairs.dom = {};
    }
}

const cleanBlocklyXml = x => x ? String(x).replace(/^\s*<xml[^>]*>/i, "").replace(/<\/xml>\s*$/i, "").trim() : "";

function combineBlocklyXml(main, init) {
    main = cleanBlocklyXml(main); init = cleanBlocklyXml(init);
    return main || init ? `<xml xmlns="http://www.w3.org/1999/xhtml">${main}${init}</xml>` : "";
}

const buildBlocklyExplorer = code => { const { mainXml, initXml } = getBlocklyXml(code); return { "main.xml": mainXml || "", "init.xml": initXml || "" } };

function parseBlocklyXml(xml) {
    if (!xml) return null;
    if (Blockly.Xml?.textToDom) return Blockly.Xml.textToDom(xml);
    const doc = new DOMParser().parseFromString(xml, "text/xml");
    if (doc.querySelector("parsererror")) throw new Error("Invalid Blockly XML");
    return doc.documentElement;
}

function createCppBasicToolbox() {
    return {
        kind: "category",
        name: "C++ Basic",
        colour: "#4C78A8",
        contents: [
            {
                kind: "category",
                name: "Logic",
                colour: "#5C81A6",
                contents: [
                    { kind: "block", type: "controls_if" },
                    { kind: "block", type: "logic_compare" },
                    { kind: "block", type: "logic_operation" },
                    { kind: "block", type: "logic_boolean" }
                ]
            },

            {
                kind: "category",
                name: "Loops",
                colour: "#5CA65C",
                contents: [
                    {
                        kind: "block",
                        type: "controls_repeat_ext",
                        inputs: {
                            TIMES: {
                                shadow: {
                                    type: "math_number",
                                    fields: {
                                        NUM: 10
                                    }
                                }
                            }
                        }
                    },
                    {
                        kind: "block",
                        type: "controls_whileUntil"
                    },
                    {
                        kind: "block",
                        type: "controls_for"
                    }
                ]
            },

            {
                kind: "category",
                name: "Math",
                colour: "#5C68A6",
                contents: [
                    { kind: "block", type: "math_number" },
                    { kind: "block", type: "math_arithmetic" },
                    { kind: "block", type: "math_single" }
                ]
            },

            {
                kind: "category",
                name: "Text",
                colour: "#5CA68D",
                contents: [
                    { kind: "block", type: "text" },
                    { kind: "block", type: "text_print" }
                ]
            },

            {
                kind: "category",
                name: "Variables",
                custom: "VARIABLE"
            },

            {
                kind: "category",
                name: "Lists",
                colour: "#745CA6",
                contents: [
                    { kind: "block", type: "lists_create_with" },
                    { kind: "block", type: "lists_length" },
                    { kind: "block", type: "lists_getIndex" }
                ]
            }
        ]
    };
}


function createGameToolbox(blocklyConfig) {
    let gameToolbox = blocklyConfig?.toolbox;

    if (!gameToolbox) {
        return null;
    }

    if (typeof gameToolbox === "string") {
        try {
            gameToolbox = JSON.parse(gameToolbox);
        } catch (e) {
            console.error("Game toolbox JSON error:", e);
            return null;
        }
    }

    if (!gameToolbox) {
        return null;
    }

    // Already a category
    if (
        gameToolbox.kind === "category" &&
        Array.isArray(gameToolbox.contents)
    ) {
        return {
            ...gameToolbox,
            name: gameToolbox.name || "Game API"
        };
    }

    // categoryToolbox
    if (
        gameToolbox.kind === "categoryToolbox" &&
        Array.isArray(gameToolbox.contents)
    ) {
        return {
            kind: "category",
            name: "Game API",
            colour: "#A65C81",
            contents: gameToolbox.contents
        };
    }

    // Raw array
    if (Array.isArray(gameToolbox)) {
        return {
            kind: "category",
            name: "Game API",
            colour: "#A65C81",
            contents: gameToolbox
        };
    }

    return null;
}



function BlocklyEditor({ workspaceXml, blocklyConfig, generator, onCodeChange, onWorkspaceChange }) {
    const divRef = useRef(null), workspaceRef = useRef(null), callbacks = useRef({ onCodeChange, onWorkspaceChange, generator });

    const lastToolboxRef = useRef(null);

    useEffect(() => { callbacks.current = { onCodeChange, onWorkspaceChange, generator } }, [onCodeChange, onWorkspaceChange, generator]);

    useEffect(() => {
        if (!divRef.current) return;

        workspaceRef.current?.dispose();


        // C++ specific toolbox
        const cppToolbox = createCppBasicToolbox();

        let gameToolbox = lastToolboxRef.current;

        if (blocklyConfig?.toolbox) {
            let rawToolbox = blocklyConfig.toolbox;

            if (typeof rawToolbox === "string") {
                try {
                    rawToolbox = JSON.parse(rawToolbox);
                } catch (e) {
                    console.error("Game toolbox JSON error:", e);
                    rawToolbox = null;
                }
            }

            if (rawToolbox) {
                let contents = [];

                // Database format:
                // {
                //     kind: "categoryToolbox",
                //     contents: [...]
                // }
                if (rawToolbox.kind === "categoryToolbox") {
                    contents = rawToolbox.contents || [];
                }

                // Database format:
                // {
                //     kind: "category",
                //     ...
                // }
                else if (rawToolbox.kind === "category") {
                    contents = [rawToolbox];
                }

                // In case database contains just an array
                else if (Array.isArray(rawToolbox)) {
                    contents = rawToolbox;
                }

                if (contents.length > 0) {
                    gameToolbox = {
                        kind: "category",
                        name: "Game Specific",
                        colour: "#A65C5C",
                        contents: contents
                    };

                    // Save only a valid toolbox
                    lastToolboxRef.current = gameToolbox;
                }
            }
        }

        //console.log("SIMPLE IDE. gameToolbox:", gameToolbox);

        const toolbox = {
            kind: "categoryToolbox",
            contents: [
                cppToolbox,
                ...(gameToolbox ? [gameToolbox] : [])
            ]
        };

        try {
            const workspace = Blockly.inject(divRef.current, {
                toolbox,
                trashcan: true,
                scrollbars: true,
                zoom: { controls: true, wheel: true, startScale: 1, maxScale: 2, minScale: .5, scaleSpeed: 1.1 },
                grid: { spacing: 20, length: 3, colour: "#444", snap: true },
                theme: Blockly.Themes?.Dark
            });

            workspaceRef.current = workspace;

            if (workspaceXml) {
                try {
                    const dom = parseBlocklyXml(workspaceXml);
                    if (dom) Blockly.Xml.domToWorkspace(dom, workspace);
                } catch (e) { console.error("Unable to restore Blockly XML:", e) }
            }

            const resize = () => workspaceRef.current && Blockly.svgResize(workspaceRef.current);
            const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
            ro?.observe(divRef.current);
            const timer = setTimeout(resize, 100);

            return () => {
                clearTimeout(timer);
                ro?.disconnect();
                workspace.dispose();
                workspaceRef.current = null;
            };
        } catch (e) { console.error("Unable to create Blockly workspace:", e) }
    }, [blocklyConfig]);

    useEffect(() => {
        const workspace = workspaceRef.current;
        if (!workspace) return;

        const change = e => {
            if (e?.isUiEvent || e?.type === Blockly.Events?.FINISHED_LOADING) return;
            try {
                const xml = Blockly.Xml.domToText(Blockly.Xml.workspaceToDom(workspace));
                const c = callbacks.current;
                c.onCodeChange?.(c.generator?.workspaceToCode?.(workspace) || "");
                c.onWorkspaceChange?.(xml);
            } catch (e) { console.error("Blockly serialization error:", e) }
        };

        workspace.addChangeListener(change);
        return () => workspace.removeChangeListener(change);
    }, []);

    useEffect(() => {
        const workspace = workspaceRef.current;
        if (!workspace || !workspaceXml) return;
        try {
            Blockly.Events.disable();
            workspace.clear();
            const dom = parseBlocklyXml(workspaceXml);
            if (dom) Blockly.Xml.domToWorkspace(dom, workspace);
        } catch (e) {
            console.error("Unable to update Blockly XML:", e);
        } finally {
            Blockly.Events.enable();
        }
    }, [workspaceXml]);

    return <div style={{ width: "100%", height: "100%", position: "relative", background: "#1e1e1e" }}>
        <div ref={divRef} style={{ width: "100%", height: "100%" }} />
        <div style={{ position: "absolute", bottom: 10, right: 15, background: "rgba(20,20,20,.9)", border: "1px solid #444", borderRadius: 4, padding: "5px 9px", fontSize: 11, color: "#aaa", pointerEvents: "none" }}>
            Graphical Editor
        </div>
    </div>;
}

function FileTree({ treeData, selectedKeys = [], onSelect, onRightClick }) {
    const [expanded, setExpanded] = useState(new Set());

    useEffect(() => {
        if (!selectedKeys.length) return;
        setExpanded(prev => {
            const next = new Set(prev);
            selectedKeys.forEach(path => {
                let current = "";
                normalizePath(path).split("/").filter(Boolean).slice(0, -1).forEach(p => {
                    current = current ? `${current}/${p}` : p; next.add(current);
                });
            });
            return next;
        });
    }, [selectedKeys]);

    const toggle = useCallback(key => setExpanded(prev => {
        const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
    }), []);

    const render = useCallback((nodes, level = 0) => nodes.map(node => {
        const open = expanded.has(node.key), selected = selectedKeys.includes(node.key);
        return <React.Fragment key={node.key}>
            <div onClick={() => node.isFolder ? toggle(node.key) : onSelect?.([node.key], { node })}
                onContextMenu={e => { e.preventDefault(); onRightClick?.({ event: e, node }) }}
                style={{ display: "flex", alignItems: "center", height: 26, paddingLeft: 6 + level * 18, cursor: "pointer", userSelect: "none", color: "#ddd", background: selected ? "#264f78" : "transparent", fontSize: 13 }}>
                <span style={{ width: 18 }}>{node.isFolder ? (open ? "▾" : "▸") : ""}</span>
                <span style={{ width: 20 }}>{node.isFolder ? "📁" : "📄"}</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.title}</span>
            </div>
            {node.isFolder && open && node.children?.length > 0 && render(node.children, level + 1)}
        </React.Fragment>;
    }), [expanded, selectedKeys, toggle, onSelect, onRightClick]);

    return <div style={{ width: "100%", overflow: "auto" }}>{render(treeData)}</div>;
}

function ProjectExplorer({ files, logs, selectedFilePath, selectedLog, onFileSelect, onLogSelect, onNewFile, onNewFolder, onDelete, onRename, readOnly = false }) {
    const [contextMenu, setContextMenu] = useState(null), treeData = useMemo(() => buildTree(files), [files]);

    useEffect(() => { const close = () => setContextMenu(null); document.addEventListener("click", close); return () => document.removeEventListener("click", close) }, []);


    const menuAction = action => {
        if (!contextMenu || !action) return;
        action(contextMenu.path, contextMenu.node); setContextMenu(null);
    };

    return <div style={{ ...styles.panel("#181818"), display: "flex", flexDirection: "column", minHeight: 0 }}>
        <PanelHeader title="EXPLORER" actions={!readOnly && <>
            <button className="btn btn-sm btn-dark" onClick={onNewFile}>+</button>
            <button className="btn btn-sm btn-dark" onClick={onNewFolder}>📁</button>
        </>} />

        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: 8 }}>
            <SectionTitle>FILES</SectionTitle>
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {treeData.length ? <FileTree
                    treeData={treeData}
                    selectedKeys={selectedFilePath ? [selectedFilePath] : []}
                    onSelect={onFileSelect}
                    onRightClick={!readOnly ? ({ event, node }) => setContextMenu({ x: event.clientX, y: event.clientY, path: node.key, node }) : undefined}
                /> : <div style={styles.muted}>No files</div>}
            </div>

            <SectionTitle style={{ marginTop: 20 }}>LOG</SectionTitle>
            <div style={{ height: 180, minHeight: 100, overflowY: "auto" }}>
                {logs.map(([timestamp, log]) => <div
                    key={timestamp}
                    onClick={() => onLogSelect?.(timestamp, log)}
                    style={{ cursor: "pointer", padding: 6, borderRadius: 3, background: selectedLog === timestamp ? "#333" : "transparent" }}
                >
                    {log?.icon === "ok" ? "✓" : "•"}
                    <span style={{ marginLeft: 7, fontSize: 12 }}>{log?.title || "Log"}</span>
                    {log?.simId && <div style={{ marginLeft: 20, ...styles.smallMuted }}>Simulation ID: {log.simId}</div>}
                </div>)}
            </div>
        </div>

        {contextMenu && <div onClick={e => e.stopPropagation()} style={{ position: "fixed", left: contextMenu.x, top: contextMenu.y, background: "#252526", border: "1px solid #444", zIndex: 9999, minWidth: 160, padding: 4 }}>
            {[["New File", onNewFile], ["New Folder", onNewFolder], ["Rename", onRename], ["Delete", onDelete]].map(([label, action]) =>
                <button key={label} className={`btn btn-sm w-100 text-start ${label === "Delete" ? "text-danger" : "text-light"}`} onClick={() => menuAction(action)}>{label}</button>
            )}
        </div>}
    </div>;
}
function PanelHeader({ title, actions }) { return <div style={styles.header}><span style={styles.headerTitle}>{title}</span><div className="d-flex gap-1">{actions}</div></div> }
function SectionTitle({ children, style }) { return <div style={{ ...styles.sectionTitle, ...style }}>{children}</div> }
function CenterText({ children }) { return <div style={styles.center}><span style={styles.muted}>{children}</span></div> }

function CodeEditor({ filePath, content, mode, workspaceXml, blocklyConfig, blocklyGenerator, onChange, onBlocklyCodeChange, onWorkspaceChange, showGeneratedCode }) {
    const graphical = mode === "graphical";
    return <div style={styles.editor}>
        <PanelHeader title={graphical ? (filePath ? `📄 ${filePath}` : "Graphical Editor") : (filePath ? `📄 ${filePath}` : "Text Editor")}
            actions={<span style={{ fontSize: 10, color: "#8ab4f8" }}>{graphical ? "BLOCKLY" : "MONACO"}</span>} />
        <div style={styles.fill}>
            {graphical && !showGeneratedCode ? <BlocklyEditor workspaceXml={workspaceXml} blocklyConfig={blocklyConfig} generator={blocklyGenerator}
                onCodeChange={onBlocklyCodeChange} onWorkspaceChange={onWorkspaceChange} />
                : filePath ? <Editor height="100%" width="100%" theme="vs-dark" language={getLanguage(filePath)} value={content || ""}
                    onChange={onChange} options={{ automaticLayout: true, minimap: { enabled: true }, fontSize: 14, scrollBeyondLastLine: false, wordWrap: "off", padding: { top: 10 } }} />
                    : <CenterText>Select a file</CenterText>}
        </div>
    </div>;
}

function SimulationVisualization({ simulationId, simulationResult }) {
    return <div style={styles.panel("#101010")}><PanelHeader title="VISUALIZATION" /><div style={styles.fill}>
        {simulationId ? <SimpleIDESimulation simulationId={simulationId} /> : simulationResult ? <CenterText>Simulation completed.</CenterText> : <CenterText>Run simulation to view the visualization.</CenterText>}
    </div></div>;
}

function ConsolePanel({ output }) {
    return <div style={styles.console}><PanelHeader title="CONSOLE" />
        <pre style={{ flex: 1, margin: 0, padding: "10px 12px", overflow: "auto", whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 12 }}>{output || "Ready."}</pre>
    </div>;
}

function NewIde({ projectId, mode, project }) {

    const editorMode = getEditorMode(project);
    const game = useMemo(() => getGameConfig(project), [project]);
    const blocklyGenerator = useMemo(() => getBlocklyGenerator(game.blocklyConfig), [game.blocklyConfig]);

    const [compiling, setCompiling] = useState(false), [files, setFiles] = useState({}),
        [selectedFilePath, setSelectedFilePath] = useState(null), [editorContent, setEditorContent] = useState(""),
        [selectedLog, setSelectedLog] = useState(null), [consoleOutput, setConsoleOutput] = useState("Ready."),
        [simulationResult, setSimulationResult] = useState(null), [saving, setSaving] = useState(false),
        [mainXml, setMainXml] = useState(""), [initXml, setInitXml] = useState(""),
        [selectedBlocklyPage, setSelectedBlocklyPage] = useState("main.xml"), [blocklyXml, setBlocklyXml] = useState(""),
        [simulationId, setSimulationId] = useState(null), [logs, setLogs] = useState([]);
    const [showGeneratedCode, setShowGeneratedCode] = useState(false);


    const mountedRef = useRef(true), compilationTimerRef = useRef(null), simulationTimerRef = useRef(null),
        latestFilesRef = useRef(files), latestMainXmlRef = useRef(mainXml), latestInitXmlRef = useRef(initXml),
        latestBlocklyXmlRef = useRef(blocklyXml), latestBlocklyPageRef = useRef(selectedBlocklyPage),
        latestEditorContentRef = useRef(editorContent), latestSelectedFileRef = useRef(selectedFilePath);

    const ideProjectRef = useRef(null);
    const [ideProjectVersion, setIdeProjectVersion] = useState(0);

    const updateIdeProject = useCallback((updater) => {
        const current = ideProjectRef.current;

        if (!current) {
            return;
        }

        try {
            updater(current);

            // Force React to know that the working object changed.
            setIdeProjectVersion(version => version + 1);
        } catch (error) {
            console.error("Unable to update IDE project:", error);
        }
    }, []);

    useEffect(() => { latestFilesRef.current = files }, [files]);
    useEffect(() => { latestMainXmlRef.current = mainXml }, [mainXml]);
    useEffect(() => { latestInitXmlRef.current = initXml }, [initXml]);
    useEffect(() => { latestBlocklyXmlRef.current = blocklyXml }, [blocklyXml]);
    useEffect(() => { latestBlocklyPageRef.current = selectedBlocklyPage }, [selectedBlocklyPage]);
    useEffect(() => { latestEditorContentRef.current = editorContent }, [editorContent]);
    useEffect(() => { latestSelectedFileRef.current = selectedFilePath }, [selectedFilePath]);

    useEffect(() => () => { mountedRef.current = false; clearTimeout(compilationTimerRef.current); clearTimeout(simulationTimerRef.current) }, []);

    const projectName = project?.name || project?.title || project?.projectname || "Project";

    useEffect(() => {
        if (!projectId || !project) {
            return;
        }

        let cancelled = false;

        const load = async () => {
            try {
                setConsoleOutput("Loading project...");

                // --------------------------------------------------------
                // Create ONE working Project.code object.
                //
                // This is now the source of truth for the entire IDE.
                // --------------------------------------------------------

                const workingProject = createCommonProjectCode(project);
                //console.log("SIMPLE IDE.  workingProject  ", workingProject);

                if (cancelled) {
                    return;
                }

                ideProjectRef.current = workingProject;

                // --------------------------------------------------------
                // GRAPHICAL PROJECT
                // --------------------------------------------------------

                if (editorMode === "graphical") {
                    const {
                        mainXml,
                        initXml
                    } = getBlocklyXml(workingProject);

                    if (cancelled) {
                        return;
                    }

                    setMainXml(mainXml);
                    setInitXml(initXml);

                    latestMainXmlRef.current = mainXml;
                    latestInitXmlRef.current = initXml;

                    const page = "main.xml";
                    const xml = mainXml || "";

                    setSelectedBlocklyPage(page);
                    latestBlocklyPageRef.current = page;

                    setBlocklyXml(xml);
                    latestBlocklyXmlRef.current = xml;

                    // Explorer displays Blockly pages.
                    const explorerFiles = buildBlocklyExplorer(
                        workingProject
                    );

                    setFiles(explorerFiles);
                    latestFilesRef.current = explorerFiles;

                    setSelectedFilePath(page);
                    latestSelectedFileRef.current = page;

                    setEditorContent("");
                    latestEditorContentRef.current = "";

                    setLogs(getProjectLogs(workingProject));
                    setSelectedLog(null);

                    setConsoleOutput(
                        `Loaded ${game.displayName || "Blockly project"}`
                    );

                    return;
                }

                // --------------------------------------------------------
                // TEXT PROJECT
                //
                // IMPORTANT:
                // Do NOT fetch Git files here.
                //
                // Project.code.files is the source of truth.
                // --------------------------------------------------------

                const projectFiles = workingProject.files || {};


                if (cancelled) {
                    return;
                }

                setFiles(projectFiles);
                latestFilesRef.current = projectFiles;

                const mainPath =
                    game.mainFile &&
                        getFileFromTree(
                            projectFiles,
                            game.mainFile
                        ) !== null
                        ? game.mainFile
                        : findFirstFile(projectFiles);

                if (mainPath) {
                    const content =
                        getFileFromTree(
                            projectFiles,
                            mainPath
                        ) || "";

                    setSelectedFilePath(mainPath);
                    latestSelectedFileRef.current = mainPath;

                    setEditorContent(content);
                    latestEditorContentRef.current = content;
                } else {
                    setSelectedFilePath(null);
                    latestSelectedFileRef.current = null;

                    setEditorContent("");
                    latestEditorContentRef.current = "";
                }

                setMainXml("");
                setInitXml("");

                latestMainXmlRef.current = "";
                latestInitXmlRef.current = "";

                setBlocklyXml("");
                latestBlocklyXmlRef.current = "";

                setSelectedBlocklyPage("main.xml");
                latestBlocklyPageRef.current = "main.xml";

                setLogs(getProjectLogs(workingProject));
                setSelectedLog(null);

                setConsoleOutput(
                    `Loaded ${game.displayName || "project"}`
                );

            } catch (error) {
                console.error(
                    "Unable to load project:",
                    error
                );

                if (!cancelled) {
                    setConsoleOutput(
                        `Unable to load project: ${error?.message || "Unknown error"
                        }`
                    );
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };

    }, [
        projectId,
        project,
        editorMode,
        game.mainFile,
        game.displayName
    ]);

    const handleEditorChange = useCallback((value) => {
        const content = value || "";
        const path = latestSelectedFileRef.current;

        if (!path || !ideProjectRef.current) return;

        ideProjectRef.current.files = setFileInTree(
            ideProjectRef.current.files || {},
            path,
            content
        );

        setFiles(ideProjectRef.current.files);
        latestFilesRef.current = ideProjectRef.current.files;

        // addProjectLog({
        //     user: user?.name || "",
        //     title: "Started editing",
        //     content: path,
        // });
    }, [addProjectLog]);

    const handleBlocklyCodeChange = useCallback((code) => {
        if (!ideProjectRef.current) {
            return;
        }

        const generatedCode = code || "";

        const mainFile =
            game.mainFile ||
            (
                game.robotType === "spheres"
                    ? "src/main.cpp"
                    : game.robotType === "astrobee"
                        ? "src/Main.java"
                        : "src/main.py"
            );

        ideProjectRef.current.files =
            setFileInTree(
                ideProjectRef.current.files || {},
                mainFile,
                generatedCode
            );

        setFiles(
            ideProjectRef.current.files
        );

        latestFilesRef.current =
            ideProjectRef.current.files;

    }, [game.mainFile, game.robotType]);

    const handleBlocklyWorkspaceChange = useCallback((xml) => {
        const value = xml || "";
        const page = latestBlocklyPageRef.current;

        setBlocklyXml(value);
        latestBlocklyXmlRef.current = value;

        if (page === "main.xml") {
            setMainXml(value);
            latestMainXmlRef.current = value;
        } else {
            setInitXml(value);
            latestInitXmlRef.current = value;
        }

        // ------------------------------------------------------------
        // Update the common Project.code object.
        // ------------------------------------------------------------

        if (ideProjectRef.current) {
            updateIdeProject(projectCode => {
                setBlocklyPageXml(
                    projectCode,
                    page,
                    value
                );
            });
        }

        // ------------------------------------------------------------
        // Keep the UI explorer in sync.
        // ------------------------------------------------------------

        setFiles(prev => {
            const updated = setFileInTree(
                prev,
                page,
                value
            );

            latestFilesRef.current = updated;

            return updated;
        });

        addProjectActivity({
            type: "blockly_changed",
            page: latestBlocklyPageRef.current,
        });

    }, [addProjectActivity, updateIdeProject]);

    const handleFileSelect = useCallback((_keys, info) => {
        if (!info?.node || info.node.isFolder) return;
        const path = info.node.key;

        if (editorMode === "graphical") {
            if (!["main.xml", "init.xml"].includes(path) || latestBlocklyPageRef.current === path) return;
            const xml = path === "main.xml" ? latestMainXmlRef.current : latestInitXmlRef.current;
            setSelectedBlocklyPage(path); latestBlocklyPageRef.current = path;
            setBlocklyXml(xml || ""); latestBlocklyXmlRef.current = xml || "";
            setSelectedFilePath(path);
            latestSelectedFileRef.current = path;

            setEditorContent(content);
            latestEditorContentRef.current = content;

            setSelectedLog(null);
            setConsoleOutput(`Opened ${path}`);

            addProjectActivity({
                type: "file_open",
                file: path
            });
            return;
        }

        const content = getFileFromTree(latestFilesRef.current, path);
        if (content === null) return;
        setSelectedFilePath(path);
        latestSelectedFileRef.current = path;

        setEditorContent(content);
        latestEditorContentRef.current = content;

        setSelectedLog(null);
        setConsoleOutput(`Opened ${path}`);

        addProjectActivity({
            type: "file_open",
            file: path
        });
    }, [editorMode, addProjectActivity]);

    const handleNewFile = useCallback(targetPath => {
        if (editorMode === "graphical") return;
        const name = window.prompt("File name:"); if (!name) return;
        const clean = normalizePath(name); if (!clean) return;
        const selected = targetPath || latestSelectedFileRef.current, parent = selected ? getParentPath(selected) : "";
        const path = parent ? `${parent}/${clean}` : clean, current = latestFilesRef.current;
        if (pathExists(current, path)) { window.alert("A file or folder with that name already exists."); return }
        const updated = createFileInTree(current, path, "");

        setFiles(updated);
        latestFilesRef.current = updated;

        // Update common Project.code
        updateIdeProject(projectCode => {
            projectCode.files = createFileInTree(
                projectCode.files || {},
                path,
                ""
            );
        });

        setSelectedFilePath(path);
        latestSelectedFileRef.current = path;

        setEditorContent("");
        latestEditorContentRef.current = "";

        setConsoleOutput(`Created file: ${path}`);
    }, [editorMode, updateIdeProject]);

    const handleNewFolder = useCallback(targetPath => {
        if (editorMode === "graphical") return;
        const name = window.prompt("Folder name:"); if (!name) return;
        const clean = normalizePath(name); if (!clean) return;
        const selected = targetPath || latestSelectedFileRef.current, parent = selected ? getParentPath(selected) : "";
        const path = parent ? `${parent}/${clean}` : clean, current = latestFilesRef.current;
        if (pathExists(current, path)) { window.alert("A file or folder with that name already exists."); return }
        const updated = createFolderInTree(
            current,
            path
        );

        setFiles(updated);
        latestFilesRef.current = updated;

        updateIdeProject(projectCode => {
            projectCode.files = createFolderInTree(
                projectCode.files || {},
                path
            );
        });

        setConsoleOutput(`Created folder: ${path}`);
    }, [editorMode, updateIdeProject]);

    const handleDelete = useCallback(targetPath => {
        if (editorMode === "graphical") return;
        const path = targetPath || latestSelectedFileRef.current; if (!path) return;
        if (!window.confirm(`Delete "${path}"?`)) return;
        const updated = deleteFromTree(
            latestFilesRef.current,
            path
        );

        setFiles(updated);
        latestFilesRef.current = updated;

        updateIdeProject(projectCode => {
            projectCode.files = deleteFromTree(
                projectCode.files || {},
                path
            );
        });

        if (latestSelectedFileRef.current === path) {
            setSelectedFilePath(null);
            latestSelectedFileRef.current = null;

            setEditorContent("");
            latestEditorContentRef.current = "";
        }

        setConsoleOutput(`Deleted: ${path}`);
    }, [editorMode, updateIdeProject]);

    const handleRename = useCallback(targetPath => {
        if (editorMode === "graphical") return;
        const path = targetPath || latestSelectedFileRef.current; if (!path) return;
        const oldName = getFileName(path), newName = window.prompt("New name:", oldName);
        if (!newName || newName === oldName) return;
        const clean = normalizePath(newName); if (!clean) return;
        const parent = getParentPath(path), target = parent ? `${parent}/${clean}` : clean, current = latestFilesRef.current;
        if (pathExists(current, target)) { window.alert("A file or folder with that name already exists."); return }

        const updated = renameTreeItem(current, path, clean);
        setFiles(updated);
        latestFilesRef.current = updated;

        updateIdeProject(projectCode => {
            projectCode.files = renameTreeItem(
                projectCode.files || {},
                path,
                clean
            );
        });
    }, [editorMode, updateIdeProject]);

    const handleProject = useCallback(() => {
        if (!projectId) { setConsoleOutput("Project ID is missing."); return }
        window.location.href = `/rect/app/projects/${project.lab_id}`;
    }, [projectId, project?.lab_id]);

    const saveProjectCode = (id, code) => api(
        `/api/project/${id}/code`,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ code })
        }
    );

    const saveCurrentProject = useCallback(async () => {
        if (!projectId) {
            throw new Error("Project ID is missing.");
        }

        if (!ideProjectRef.current) {
            throw new Error("Project data is not loaded.");
        }

        // ------------------------------------------------------------
        // Update the currently open text file one last time.
        //
        // This protects against the case where Monaco's state has
        // changed but the React callback has not yet processed it.
        // ------------------------------------------------------------

        if (
            editorMode === "text" &&
            latestSelectedFileRef.current
        ) {
            const path = latestSelectedFileRef.current;
            const content =
                latestEditorContentRef.current || "";

            ideProjectRef.current.files = setFileInTree(
                ideProjectRef.current.files || {},
                path,
                content
            );
        }

        // ------------------------------------------------------------
        // Update Blockly XML one last time.
        // ------------------------------------------------------------

        if (editorMode === "graphical") {
            const page =
                latestBlocklyPageRef.current || "main.xml";

            const xml =
                latestBlocklyXmlRef.current || "";

            setBlocklyPageXml(
                ideProjectRef.current,
                page,
                xml
            );
        }

        // ------------------------------------------------------------
        // Update project timestamp.
        // ------------------------------------------------------------

        ideProjectRef.current.updateTime = Date.now();

        // ------------------------------------------------------------
        // Save the COMPLETE Project.code object.
        // ------------------------------------------------------------

        const code = ideProjectRef.current;
        console.log("SIMPLE IDE code::::", code);

        await saveProjectCode(
            projectId,
            code
        );

        return code;

    }, [projectId, editorMode]);

    const handleCommit = useCallback(async () => {
        if (!projectId) {
            setConsoleOutput("Project ID is missing.");
            return;
        }

        try {
            setSaving(true);
            setConsoleOutput("Saving project...");

            await saveCurrentProject();

            if (editorMode === "graphical") {
                setConsoleOutput(
                    "Blockly project saved successfully."
                );
                return;
            }

            const result = await commitProject(
                projectId,
                `Update ${projectName}`
            );

            setConsoleOutput(
                result?.message ||
                "Project committed successfully."
            );

        } catch (error) {
            console.error("Commit error:", error);

            setConsoleOutput(
                `Save error: ${error?.message || "Unknown error"}`
            );

        } finally {
            setSaving(false);
        }

    }, [
        projectId,
        editorMode,
        projectName,
        saveCurrentProject
    ]);

    const pollCompilationStatus = useCallback(async id => {
        try {
            const result = await api(`/api/ide/compile/status/${id}`);
            if (!mountedRef.current) return;

            setConsoleOutput(`Compilation: ${result.status}${result.message ? ` - ${result.message}` : ""}`);
            const status = String(result.status || "").toUpperCase();

            if (!["COOKING", "PENDING", "RUNNING"].includes(status)) { setCompiling(false); return }

            compilationTimerRef.current = setTimeout(() => pollCompilationStatus(id), 2000);
        } catch (error) {
            console.error("Compilation status error:", error);
            if (mountedRef.current) {
                setConsoleOutput(`Compilation status error: ${error.message}`);
                setCompiling(false);
            }
        }
    }, []);

    const getSourceCode = useCallback(() => {
        const generatedProject = generateSourceCode();

        const generatedCode =
            getFileFromTree(
                generatedProject.files,
                game.mainFile
            ) || "";
        return generatedCode;
    });

    const handleShowCpp = useCallback(() => {
        try {

            const generatedCode = getSourceCode();

            setEditorContent(generatedCode);
            latestEditorContentRef.current = generatedCode;

            setShowGeneratedCode(true);

            console.log("SIMPLE IDE. generated C++:", generatedCode);

            setConsoleOutput(
                "C++ code generated from Blockly."
            );

        } catch (error) {
            console.error(
                "Blockly C++ generation error:",
                error
            );

            setConsoleOutput(
                `Unable to generate C++ code: ${error?.message || "Unknown error"
                }`
            );
        }
    }, [
        generateSourceCode,
        game.mainFile
    ]);


    const generateSourceCode = useCallback(() => {
        const project = ideProjectRef.current;

        if (!project) {
            throw new Error("IDE project is not loaded.");
        }

        // Text mode does not need Blockly generation.
        if (editorMode !== "graphical") {
            return project;
        }

        const pages = project.pages?.pairs || {};

        try {
            let generatedCode = "";

            const pageNames = Object.keys(pages);

            for (const pageName of pageNames) {
                const page = pages[pageName];
                const xml = page?.pairs?.xml || "";

                if (!xml.trim()) {
                    continue;
                }

                // ----------------------------------------------------
                // Convert XML string to DOM
                // ----------------------------------------------------

                const dom =
                    new DOMParser().parseFromString(
                        xml,
                        "text/xml"
                    );

                if (
                    !dom ||
                    !dom.documentElement ||
                    dom.documentElement.nodeName === "parsererror"
                ) {
                    throw new Error(
                        `Invalid Blockly XML in page "${pageName}".`
                    );
                }

                // ----------------------------------------------------
                // Create temporary Blockly workspace
                // ----------------------------------------------------

                const tempWorkspace =
                    new Blockly.Workspace();

                try {
                    Blockly.Xml.domToWorkspace(
                        dom.documentElement,
                        tempWorkspace
                    );

                    // ------------------------------------------------
                    // Generate source code
                    // ------------------------------------------------

                    let pageCode = "";

                    if (game.robotType === "spheres") {

                        if (!zrCppGenerator) {
                            throw new Error(
                                "SPHERES C++ generator is not available."
                            );
                        }

                        // Set C++ global variables if your generator
                        // uses them.
                        if (
                            Object.prototype.hasOwnProperty.call(
                                zrCppGenerator,
                                "C_GLOBAL_VARS"
                            )
                        ) {
                            zrCppGenerator.C_GLOBAL_VARS =
                                project.cglobals?.pairs || {};
                        }

                        // Preferred: generate complete workspace
                        if (
                            typeof zrCppGenerator.workspaceToCode ===
                            "function"
                        ) {
                            pageCode =
                                zrCppGenerator.workspaceToCode(
                                    tempWorkspace
                                );
                        } else {
                            // Fallback: generate each top-level block
                            const topBlocks =
                                tempWorkspace.getTopBlocks(true);

                            for (const block of topBlocks) {
                                const result =
                                    zrCppGenerator.blockToCode(
                                        block
                                    );

                                if (Array.isArray(result)) {
                                    pageCode +=
                                        result[0] || "";
                                } else {
                                    pageCode +=
                                        result || "";
                                }
                            }
                        }

                    } else {

                        // ------------------------------------------------
                        // Astrobee / IntBall
                        // ------------------------------------------------

                        const generator =
                            getGameGenerator(game);

                        if (!generator) {
                            throw new Error(
                                `No Blockly generator configured for ${game.robotType}`
                            );
                        }

                        if (
                            typeof generator.workspaceToCode ===
                            "function"
                        ) {
                            pageCode =
                                generator.workspaceToCode(
                                    tempWorkspace
                                );
                        } else {
                            const topBlocks =
                                tempWorkspace.getTopBlocks(true);

                            for (const block of topBlocks) {
                                const result =
                                    generator.blockToCode(
                                        block
                                    );

                                if (Array.isArray(result)) {
                                    pageCode +=
                                        result[0] || "";
                                } else {
                                    pageCode +=
                                        result || "";
                                }
                            }
                        }
                    }

                    // ------------------------------------------------
                    // Add page code
                    // ------------------------------------------------

                    generatedCode +=
                        `//Begin page ${pageName}\n` +
                        `${pageCode || ""}\n` +
                        `//End page ${pageName}\n\n`;

                } finally {
                    tempWorkspace.dispose();
                }
            }

            // --------------------------------------------------------
            // Finish SPHERES C++ code
            // --------------------------------------------------------

            if (
                game.robotType === "spheres" &&
                typeof zrCppGenerator.finishFull === "function"
            ) {
                generatedCode =
                    zrCppGenerator.finishFull(
                        generatedCode
                    );
            }

            // --------------------------------------------------------
            // Put generated source into Project.code.files
            // --------------------------------------------------------

            if (!project.files) {
                project.files = {};
            }

            const mainFile =
                game.mainFile ||
                (
                    game.robotType === "spheres"
                        ? "src/main.cpp"
                        : game.robotType === "astrobee"
                            ? "src/Main.java"
                            : "src/main.py"
                );

            project.files = setFileInTree(
                project.files,
                mainFile,
                generatedCode
            );

            return project;

        } catch (error) {
            console.error(
                "generateSourceCode error:",
                error
            );

            throw error;
        }
    }, [
        editorMode,
        game.robotType,
        game.language,
        game.mainFile,
    ]);

    const handleCompile = useCallback(async () => {
        if (!projectId) { setConsoleOutput("Project ID is missing."); return }
        if (!game.id) { setConsoleOutput("Game ID is missing."); return }
        if (!game.compileType) { setConsoleOutput("Game compile type is missing."); return }

        try {
            setCompiling(true); setConsoleOutput("Saving project...");
            await saveCurrentProject(); setConsoleOutput("Submitting compilation...");
            // gameId: model.getRoot().get('gameId'),
            // code: code,
            // compileType: "astrobee",
            // codesize: 1234

            const r = await fetch("/api/ide/compile", {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    projectId:projectId, gameId: game.id, compileType: game.compileType, code: getSourceCode(),
                    actionType: "compile", type: editorMode
                })
            });

            const text = await r.text();
            if (!r.ok) throw new Error(text || `Compile failed: ${r.status}`);

            let id = text;
            try {
                const p = JSON.parse(text);
                id = typeof p === "string" ? p : p?.id || p?.compileId || text;
            } catch { id = text.replace(/^"|"$/g, "") }

            id = String(id).trim();
            setConsoleOutput(`Compilation submitted. Compilation ID: ${id}`);
            pollCompilationStatus(id);
        } catch (error) {
            console.error("Compile error:", error);
            setConsoleOutput(`Compile error: ${error.message}`);
            setCompiling(false);
        }
    }, [projectId, game.id, game.compileType, editorMode, saveCurrentProject, pollCompilationStatus]);

    const pollSimulationStatus = useCallback(async id => {
        try {
            const result = await api(`/api/ide/simulate/status/${id}`);
            if (!mountedRef.current) return;

            setConsoleOutput(`Simulation: ${result.status}${result.message ? ` - ${result.message}` : ""}`);
            const status = String(result.status || "").toUpperCase();

            if (!["COOKING", "PENDING", "RUNNING"].includes(status)) {
                setCompiling(false); setSimulationResult(result);
                setLogs(prev => [...prev, [Date.now(), {
                    icon: result.success === false ? "error" : "ok",
                    title: "Simulation completed", simId: id,
                    content: result.message || `Simulation ${status}`
                }]]);
                return;
            }

            simulationTimerRef.current = setTimeout(() => pollSimulationStatus(id), 2000);
        } catch (error) {
            console.error("Simulation status error:", error);
            if (mountedRef.current) {
                setConsoleOutput(`Simulation status error: ${error.message}`);
                setCompiling(false);
            }
        }
    }, []);

    const handleSimulate = useCallback(async () => {
        if (!projectId) { setConsoleOutput("Project ID is missing."); return }
        if (!game.id) { setConsoleOutput("Game ID is missing."); return }
        if (!game.compileType) { setConsoleOutput("Game compile type is missing."); return }

        try {
            setSimulationId(null); setSimulationResult(null); setCompiling(true);
            setConsoleOutput("Saving project..."); await saveCurrentProject();
            setConsoleOutput("Submitting simulation...");

            const r = await fetch("/api/ide/simulate", {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    projectId, gameId: game.id, compileType: game.compileType,
                    actionType: "simulate", type: editorMode,
                    simConfig: {
                        timeout: 60,
                        state1: [0, 0, 0, 0, 0, 0],
                        state2: [0, 0, 0, 0, 0, 0],
                        gameVariables: {}
                    }
                })
            });

            const text = await r.text();
            if (!r.ok) throw new Error(text || `Simulation failed: ${r.status}`);

            let id = text;
            try {
                const p = JSON.parse(text);
                id = typeof p === "string" ? p : p?.id || p?.simulationId || text;
            } catch { id = text.replace(/^"|"$/g, "") }

            id = String(id).trim();
            setSimulationId(id); setSimulationResult({ success: true, simulationId: id });
            setConsoleOutput(`Simulation submitted. Simulation ID: ${id}`);
            pollSimulationStatus(id);
        } catch (error) {
            console.error("Simulation error:", error);
            setConsoleOutput(`Simulation error: ${error.message}`);
            setCompiling(false);
        }
    }, [projectId, game.id, game.compileType, editorMode, saveCurrentProject, pollSimulationStatus]);

    const addProjectLog = useCallback((log) => {
        if (!ideProjectRef.current) {
            return;
        }

        updateIdeProject(projectCode => {
            if (
                !projectCode.log ||
                typeof projectCode.log !== "object"
            ) {
                projectCode.log = {};
            }

            if (
                !projectCode.log.pairs ||
                typeof projectCode.log.pairs !== "object"
            ) {
                projectCode.log.pairs = {};
            }

            const timestamp = String(
                Date.now() + Math.random()
            );

            projectCode.log.pairs[timestamp] = {
                ...log
            };
        });

    }, [updateIdeProject]);

    const addProjectActivity = useCallback((activity) => {
        if (!ideProjectRef.current) {
            return;
        }

        updateIdeProject(projectCode => {
            if (
                !projectCode.activities ||
                typeof projectCode.activities !== "object"
            ) {
                projectCode.activities = {};
            }

            if (
                !projectCode.activities.pairs ||
                typeof projectCode.activities.pairs !== "object"
            ) {
                projectCode.activities.pairs = {};
            }

            const timestamp = String(
                Date.now() + Math.random()
            );

            projectCode.activities.pairs[timestamp] = {
                ...activity
            };
        });

    }, [updateIdeProject]);


    return <div style={styles.app}>
        <nav className="navbar navbar-dark bg-dark" style={{ height: 52, minHeight: 52 }}>
            <div className="container-fluid">
                <span className="navbar-brand">{projectName}</span>
                <div className="d-flex gap-2">
                    {editorMode === "graphical" && showGeneratedCode && (
                        <button
                            className="btn btn-sm btn-outline-light"
                            onClick={() => setShowGeneratedCode(false)}
                        >
                            Show Blockly
                        </button>
                    )}
                    {editorMode === "graphical" && !showGeneratedCode && (
                        <button
                            className="btn btn-sm btn-outline-light"
                            onClick={handleShowCpp}
                        >
                            Show C++
                        </button>
                    )}
                    <button className="btn btn-sm btn-outline-light" onClick={handleProject}>Project</button>
                    <button className="btn btn-sm btn-outline-light" onClick={handleCommit} disabled={saving || compiling}>{saving ? "Saving..." : "Save"}</button>
                    <button className="btn btn-sm btn-success" onClick={handleCompile} disabled={compiling || saving}>{compiling ? "Working..." : "Compile"}</button>
                    <button className="btn btn-sm btn-primary" onClick={handleSimulate} disabled={compiling || saving}>Simulate</button>
                </div>
            </div>
        </nav>

        <div style={{ flex: 1, minHeight: 0 }}>
            <Splitter direction={SplitDirection.Vertical} initialSizes={[80, 20]} style={styles.splitter}>
                <Splitter direction={SplitDirection.Horizontal} initialSizes={[20, 50, 30]} style={styles.splitter}>
                    <ProjectExplorer
                        files={files} logs={logs}
                        selectedFilePath={selectedFilePath} selectedLog={selectedLog}
                        onFileSelect={handleFileSelect}
                        onLogSelect={(timestamp, log) => { setSelectedLog(timestamp); setConsoleOutput(log?.content || log?.title || "") }}
                        onNewFile={handleNewFile} onNewFolder={handleNewFolder}
                        onDelete={handleDelete} onRename={handleRename}
                        readOnly={editorMode === "graphical"}
                    />
                    <CodeEditor
                        filePath={selectedFilePath}
                        content={editorContent}
                        mode={editorMode}
                        workspaceXml={blocklyXml}
                        blocklyConfig={game?.blocklyConfig}
                        blocklyGenerator={getBlocklyGenerator(game?.blocklyConfig)}
                        onChange={handleEditorChange}
                        onBlocklyCodeChange={handleBlocklyCodeChange}
                        onWorkspaceChange={handleBlocklyWorkspaceChange}
                        showGeneratedCode={showGeneratedCode}
                    />
                    <SimulationVisualization simulationId={simulationId} simulationResult={simulationResult} />
                </Splitter>
                <ConsolePanel output={consoleOutput} />
            </Splitter>
        </div>
    </div>;
}

function SimpleIDE({ projectId, mode, project, gameId, onProjectChange }) {



    if (!projectId) return <div>Missing project ID.</div>;
    if (!project) return <div>Loading project...</div>;
    const normalizedMode = getEditorMode(project);
    return <NewIde key={`${projectId}-${normalizedMode}`} projectId={projectId} mode={normalizedMode} project={project} />;
}

const styles = {
    app: { height: "100vh", width: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#1e1e1e" },
    splitter: { height: "100%", width: "100%" },
    panel: background => ({ height: "100%", background, color: "#ddd", display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, overflow: "hidden" }),
    editor: { height: "100%", width: "100%", display: "flex", flexDirection: "column", background: "#1e1e1e", minWidth: 0, minHeight: 0, overflow: "hidden" },
    console: { height: "100%", background: "#0d0d0d", color: "#ccc", display: "flex", flexDirection: "column", minHeight: 0 },
    header: { height: 38, minHeight: 38, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 10px", background: "#252526", borderBottom: "1px solid #333" },
    headerTitle: { fontSize: 11, fontWeight: "bold", color: "#aaa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    sectionTitle: { fontSize: 10, fontWeight: "bold", color: "#777", marginBottom: 6 },
    muted: { color: "#777", padding: 8 },
    smallMuted: { fontSize: 10, color: "#777" },
    fill: { flex: 1, minHeight: 0, minWidth: 0 },
    center: { height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, textAlign: "center" }
};

export default SimpleIDE;